import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react';
import type { FC, ReactNode } from 'react';
import { cn } from '../lib/utils';
import { DataTypeLabel } from './data-type-label';
import type { DataTableColumnMeta } from './data-table-features';

interface DataTableHeaderProps {
  /** The rendered header (usually the column name). */
  children: ReactNode;
  meta?: DataTableColumnMeta;
  /** `false` when the column does not sort. */
  sorted: false | 'asc' | 'desc';
  canSort: boolean;
  onSort?: (event: unknown) => void;
}

const CHANGE_LABEL = { added: 'Added', retyped: 'Retyped' } as const;

/** One header cell's contents: the name (a sort button when sortable), its type and any change tag. */
export const DataTableHeader: FC<DataTableHeaderProps> = ({
  children,
  meta,
  sorted,
  canSort,
  onSort
}) => {
  const SortIcon =
    sorted === 'asc' ? ArrowUp : sorted === 'desc' ? ArrowDown : ChevronsUpDown;
  const name = (
    <span
      className={cn(
        'font-medium',
        meta?.mono && 'font-mono text-[12px]',
        meta?.change === 'added'
          ? 'text-brand'
          : meta?.change === 'retyped'
            ? 'text-warning'
            : 'text-foreground'
      )}
    >
      {children}
    </span>
  );
  return (
    <span
      className={cn(
        'flex flex-col gap-0.5',
        meta?.align === 'right' ? 'items-end' : 'items-start'
      )}
    >
      {canSort ? (
        <button
          type="button"
          onClick={onSort}
          className="-mx-1 flex cursor-pointer items-center gap-1 rounded-sm px-1 outline-none hover:bg-surface-hover focus-visible:ring-2 focus-visible:ring-ring [&_svg]:size-3"
        >
          {name}
          <SortIcon
            aria-hidden="true"
            className={sorted ? 'text-brand' : 'text-muted-foreground'}
          />
        </button>
      ) : (
        name
      )}
      {meta?.type || meta?.change ? (
        // Secondary, not muted: on a tinted header it must still hold 4.5:1.
        <span className="flex items-center gap-1.5 text-[color:var(--z-text-secondary)]">
          {meta.type ? <DataTypeLabel type={meta.type} className="text-inherit" /> : null}
          {meta.change ? (
            <span className="text-eyebrow-sm uppercase">{CHANGE_LABEL[meta.change]}</span>
          ) : null}
        </span>
      ) : null}
    </span>
  );
};
